import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { putRequest, getRequest, showMessage } from '../../../../config';
import ModuleForm from "../ModuleForm";

const ModuleEdit = ({ moduleId, moduleName, onSuccess, onCancel }) => {
    const dispatch = useDispatch();
    const [moduleData, setModuleData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchModuleData();
    }, [moduleId, moduleName]);

    const fetchModuleData = async (silent = false) => {
        if (!silent) setIsLoading(true);
        try {
            // Use the new temp-modules endpoint - it returns module with sections and questions
            let response;
            if (moduleId) {
                response = await getRequest(`/modules/temp-modules/${moduleId}`);
            } else if (moduleName) {
                response = await getRequest(`/modules/temp-modules/by-name/${moduleName}`);
            }

            if (response?.code === 200) {
                // The API returns module with populated sections and questions
                const moduleData = response.body || response.body?.module || response.body?.data;
                setModuleData(moduleData);
                console.log('Loaded module with sections:', moduleData.sections?.length || 0);
            } else {
                if (!silent) showMessage('Failed to load module data');
            }
        } catch (error) {
            console.error('Error fetching module:', error);
            if (!silent) showMessage('Failed to load module data');
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    const handleFormChange = (formData) => {
        // Form data changes are handled internally by ModuleForm
    };

    const handleSubmit = async (formData) => {
        setIsSubmitting(true);
        try {
            // Use the new temp-modules update endpoint (PUT method)
            // This will update module and replace all sections/questions
            const updateData = {
                ...formData,
                _id: moduleId
            };

            // Use PUT request for update
            const response = await putRequest('/modules/update-temp-module', updateData);

            if (response?.code === 200) {
                const result = response.body || response;
                showMessage(response.message || 'Module updated successfully!');

                // Log update summary
                console.log(`Updated: ${result.sectionsUpdated || 0} sections, ${result.questionsUpdated || 0} questions`);

                if (onSuccess) {
                    onSuccess(result);
                }
                if (onCancel) {
                    onCancel();
                }
            } else {
                showMessage(response.message || 'Failed to update module');
            }
        } catch (error) {
            console.error('Error updating module:', error);
            showMessage('Failed to update module. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            // Default behavior: navigate back
            window.history.back();
        }
    };

    if (isLoading) {
        return (
            <div className="df jcc aic p4rem">
                <div className="loader"></div>
                <span className="ml1rem fs1rem c15">Loading module data...</span>
            </div>
        );
    }

    if (!moduleData) {
        return (
            <div className="df fdc aic jcc p4rem">
                <p className="fs1-25rem c15 fw500 mb1rem">Module not found</p>
                <button
                    onClick={handleCancel}
                    className="bg1 bo4 bss bw1px br5px p1rem fw600 c4 td250 cp"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="modules-container" style={{ padding: '2rem' }}>
            <ModuleForm
                moduleData={moduleData}
                onFormChange={handleFormChange}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isEdit={true}
                onPriorityComplete={() => fetchModuleData(true)}
            />
        </div>
    );
};

export default ModuleEdit;
